import {createApp, h, nextTick, ref} from "vue";

import Layout from "@components/Layout/Layout.vue";
import {createInertiaApp, Link, router, usePage} from "@inertiajs/vue3";
import {restartActivityTracking, startActivityTracking} from "@pharma4u/medicheck-activity-tracker";
import * as Sentry from "@sentry/vue";
import ClickOutsideDirective from "@utils/Directives/ClickOutside.js";
import Broadcast from "@utils/Helpers/Broadcast";
import PrivacyVault from "@utils/PrivacyVault";
import Axios from "axios";
import {debounce} from "es-toolkit";
import Cookies from "js-cookie";
import {ZiggyVue} from "ziggy-js";
import "@css/frontend.css";

import {initDeveloperMode} from "@/resources/js/Composable/useDeveloperMode.js";
import useTestId from "@/resources/js/Composable/useTestId.js";

createInertiaApp({
  /** TODO: add progressbar */
  progress: {
    color: "#b71c1c",
  },

  resolve: async (name) => {
    const pages = await import.meta.glob("./Pages/**/*.vue", {eager: true});
    let page = pages[`./Pages/${name}.vue`];

    if (name === "Error/Show") {
      return page;
    }

    page.default.layout = page.default.layout || Layout;

    restartActivityTracking();

    return page;
  },

  title: (title) =>
    title ? `${title} - MediCheck | AMTS schnell und effizient` : "MediCheck | AMTS schnell und effizient",

  setup({App, el, plugin, props}) {
    const privacy = new PrivacyVault(props.initialPage.props);

    const axios = Axios.create({
      withCredentials: true,
      withXSRFToken: true,
    });

    axios.interceptors.request.use(function (config) {
      const token = Cookies.get("mc_token");
      if (token) {
        config.headers["Authorization"] = "Bearer " + token;
      }
      return config;
    });

    // keep track of pending XHR requests
    const pendingAjaxRequests = ref(0);
    router.on("start", () => {
      pendingAjaxRequests.value++;
    });

    router.on("finish", () => {
      pendingAjaxRequests.value--;
    });

    const app = createApp({
      render: () => h(App, props),
    }).use(plugin, ZiggyVue);

    app.config.globalProperties.$route = route;
    app.provide("$privacy", privacy);
    app.provide("$axios", axios);
    app.provide("$pendingAjaxRequests", pendingAjaxRequests);

    // for component library
    app.provide("inertiaLink", Link);

    if (props.initialPage.props.client) {
      app.provide("$broadcast", new Broadcast(props.initialPage.props.client.id));
    }

    app.directive("click-outside", ClickOutsideDirective);

    // Sentry if dsn is set
    if (import.meta.env.SENTRY_VUE_DSN) {
      Sentry.init({
        app,
        dsn: import.meta.env.SENTRY_VUE_DSN,
        environment: import.meta.env.APP_ENV,
        debug: true,
      });
    }

    // matomo
    if (globalThis._mtm !== undefined) {
      router.on("navigate", (event) => {
        // Delay to allow components to mount and set title
        setTimeout(() => {
          const {props} = usePage();

          // license can be empty while logging in for example
          const licenseName = props?.client?.license
            ? props.client.license.product_name + " " + props.client.license.version_name
            : "Unknown";

          globalThis._mtm.push({
            event: "navigate",
            mcPageUrl: event.detail.page.url,
            mcLicence: licenseName,
          });
        }, 100);
      });
    }

    // global event for breakpoint changes
    // 1st: const with tailwind screenSizes
    const screenSizes = [
      {breakpoint: "2xl", width: 1536},
      {breakpoint: "xl", width: 1280},
      {breakpoint: "lg", width: 1024},
      {breakpoint: "md", width: 768},
      {breakpoint: "sm", width: 640},
    ];

    let changeBreakpointEvent = new CustomEvent("breakpoint", {
      detail: {
        breakpoint: "sm",
      },
    });

    // 2nd: always emit a first "resize" event on initial load
    // end on every "navigate" event
    for (let index = 0, length = screenSizes.length; index < length; ++index) {
      if (window.innerWidth >= screenSizes[index].width) {
        changeBreakpointEvent.detail.breakpoint = screenSizes[index].breakpoint;
        break; // for
      }
    }
    router.on("navigate", () => {
      setTimeout(() => {
        globalThis.dispatchEvent(changeBreakpointEvent);
      }, 2000);
    });

    // initialize developer mode
    initDeveloperMode(app);

    // 3rd: add event listener on resize (but with a solid debounce)
    //      and trigger the largest valid breakpoint
    window.addEventListener(
      "resize",
      debounce(() => {
        for (let index = 0, length = screenSizes.length; index < length; ++index) {
          if (window.innerWidth >= screenSizes[index].width) {
            changeBreakpointEvent.detail.breakpoint = screenSizes[index].breakpoint;
            break; // for
          }
        }

        globalThis.dispatchEvent(changeBreakpointEvent);
      }, 500),
    );

    document.addEventListener("mc-activity:track", (event) => {
      if (event.detail?.passedTime) {
        axios.post(route("api.activity"), {
          duration: event.detail.passedTime,
          user_id:
            privacy.getCurrentUser()?.id ??
            (typeof crypto.randomUUID === "function"
              ? crypto.randomUUID() // randomUUID() is only available in a secure context
              : Math.random().toString(36).slice(2, 15)), // fallback to a random string
        });
      }
    });

    startActivityTracking();

    const anchorTimeout = ref(null);

    function scrollToAndHighlightAnchor() {
      if (anchorTimeout.value !== null) {
        clearTimeout(anchorTimeout.value);
        anchorTimeout.value = null;
      }

      anchorTimeout.value = setTimeout(() => {
        const hash = globalThis.location.hash;
        if (hash) {
          try {
            const target = document.querySelector(hash);
            if (target) {
              nextTick(() => {
                target.scrollIntoView();
                if (target.getBoundingClientRect().top < 80) {
                  const offset = 80; // offset of fixed header height
                  window.scrollBy(0, -offset);
                }
                // highlight with glow animation (add "animate-none!" to element to avoid it)
                target.classList.add("animate-glow");
                setTimeout(() => {
                  target.classList.remove("animate-glow");
                }, 2000);
                // removes #anchor from url and _replaces_ it in history, otherwise inertia actions (save/update)
                // on the page will result in an additional history entry and back would re-open the anchor again
                // instead of the previous page
                // we manually overwrite the state.page.url because a
                // router.replace(window.location.href.split("#")[0], {preserveScroll: true});
                // could result in a redirect loop
                if (globalThis?.history?.state?.page?.url)
                  globalThis.history.state.page.url = globalThis.location.pathname;
              });
            }
          } catch {
            // nothing
            console.error("Ignoring invalid selector '" + hash + "'");
          }
        }
      }, 300);
    }

    app.mixin({
      props: {
        testId: {
          type: String,
          default: "",
        },
      },

      beforeCreate() {
        const {getTestId} = useTestId(this.testId || "");
        this.getTestId = getTestId;
      },

      mounted() {
        // avoid calling this mounted() action in EVERY component,
        // we only want to scroll to anchors on page components
        const componentName = this.$options?.name ?? "";
        if (
          componentName.startsWith("Page") &&
          (componentName.endsWith("Index") ||
            componentName.endsWith("Show") ||
            componentName.endsWith("Create") ||
            componentName.endsWith("Edit"))
        ) {
          scrollToAndHighlightAnchor();
        }
      },
    });

    router.on("navigate", () => {
      scrollToAndHighlightAnchor();
    });

    router.on("error", (event) => {
      console.error(event.detail.errors);
    });

    app.mount(el);
    return app;
  },
});
